import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, CheckCircle, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

export const MealSuggestionTrigger: React.FC = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const runSuggestions = async () => {
    setIsRunning(true);
    setError(null);
    setSuccess(null);

    try {
      const { data, error } = await supabase.functions.invoke('trigger-meal-suggestions-cron', {
        body: {}
      });

      if (error) {
        console.error('Error:', error);
        setError(error.message || 'Failed to trigger cron job');
        toast.error('Failed to trigger cron job: ' + (error.message || 'Unknown error'));
      } else {
        setSuccess('Meal suggestions cron job triggered successfully. Check the Cron Job Execution History below for results.');
        toast.success('Meal suggestions cron job triggered successfully!');
      }
    } catch (err: any) {
      console.error('Failed to trigger cron job:', err);
      setError(err.message || 'Failed to trigger cron job');
      toast.error('Failed to trigger cron job: ' + (err.message || 'Unknown error'));
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5" />
            Trigger Meal Suggestions
          </CardTitle>
          <CardDescription>
            Manually trigger the meal suggestions cron job to generate meal suggestions for all eligible families.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button 
            onClick={runSuggestions} 
            disabled={isRunning}
            className="w-full"
          >
            {isRunning ? 'Triggering Suggestions...' : 'Trigger Meal Suggestions'}
          </Button>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <span className="text-sm text-destructive">{error}</span>
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-sm text-green-800">{success}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};